import { CloudArrowUpIcon } from "@phosphor-icons/react";
import { Dropzone } from "@/src/components/ui/dropzone";
import { useState } from "react";
import { useTranslation } from "@/src/i18n/client";
import { toast } from "sonner";
import { FileDuplicateWarning } from "@/src/components/manage/files/FileDuplicateWarning";
import { checkFileDuplicateInOrganization, type FileDuplicateInfo } from "@/src/api/file-duplicate-check";
import { computeFileHash } from "@/src/lib/file";

interface FileUploadAreaProps {
  onFilesChanged: (files: File[]) => void;
  disabled?: boolean;
}

const MAX_FILES = 10;

export function FileUploadArea({ onFilesChanged, disabled }: FileUploadAreaProps) {
  const { t } = useTranslation();
  const [currentFiles, setCurrentFiles] = useState<File[]>([]);

  // Duplicate detection state
  const [duplicateWarningOpen, setDuplicateWarningOpen] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<FileDuplicateInfo | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const handleFilesChanged = async (updatedFiles: File[]) => {
    if (updatedFiles.length > MAX_FILES) {
      toast.error(t('homePage.uploadArea.maxFiles', `You can upload a maximum of ${MAX_FILES} files at once.`));
      updatedFiles = updatedFiles.slice(0, MAX_FILES);
    }

    // Check for duplicates in organization (only for new files added)
    const newFiles = updatedFiles.filter(f => !currentFiles.some(cf => cf.name === f.name && cf.size === f.size));

    if (newFiles.length > 0) {
      try {
        const fileToCheck = newFiles[0];
        const hash = await computeFileHash(fileToCheck);
        const duplicateCheck = await checkFileDuplicateInOrganization({ data: { hash } });

        if (duplicateCheck.exists) {
          setDuplicateInfo(duplicateCheck);
          setPendingFiles(updatedFiles);
          setDuplicateWarningOpen(true);
          return;
        }
      } catch (error) {
        console.error('Failed to check for duplicate files:', error);
      }
    }

    setCurrentFiles(updatedFiles);
    onFilesChanged(updatedFiles);
  };

  const handleContinueWithDuplicate = () => {
    setCurrentFiles(pendingFiles);
    onFilesChanged(pendingFiles);
    setPendingFiles([]);
    setDuplicateInfo(null);
  };

  return (
    <>
      <Dropzone
        className="mb-6 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-input p-5 sm:p-8 text-center hover:border-primary dark:border-input dark:hover:border-primary"
        accept="image/*,application/pdf"
        maxSizeMB={10}
        maxFiles={MAX_FILES}
        onFilesChanged={handleFilesChanged}
        files={currentFiles}
        disabled={disabled}
      >
        <CloudArrowUpIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <p className="mb-2 text-sm text-muted-foreground wrap-break-word">
          <span className="font-semibold">{t('homePage.uploadArea.title')}</span> {t('homePage.uploadArea.dragAndDrop')}
        </p>
        <p className="text-xs text-muted-foreground wrap-break-word">
          {t('homePage.uploadArea.fileTypes')}
        </p>
      </Dropzone>

      {duplicateInfo && (
        <FileDuplicateWarning
          open={duplicateWarningOpen}
          onOpenChange={setDuplicateWarningOpen}
          duplicateInfo={duplicateInfo}
          onContinue={handleContinueWithDuplicate}
          fileName={pendingFiles[0]?.name || ''}
        />
      )}
    </>
  );
}
